import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BarChart3, ShieldCheck, ArrowRight, LogOut, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Presentación por panel. Las clases son las mismas que usa WelcomeMessage
// para las tarjetas de quick action, así el selector se siente parte de la app.
const PANEL_UI = {
  diagnostico: { icon: BarChart3, iconBg: 'bg-[#00B7D4]' },
  admin: { icon: ShieldCheck, iconBg: 'bg-slate-700' },
};

// "Cobranzas, Carga de Documentos y 3 más"
const resumirHerramientas = (labels) => {
  if (labels.length === 0) return null;
  if (labels.length <= 2) return labels.join(' y ');
  return `${labels.slice(0, 2).join(', ')} y ${labels.length - 2} más`;
};

const PanelesPage = () => {
  const { user, token, logout, paneles, hasAdminTool } = useAuth();
  const navigate = useNavigate();
  const [resumenAdmin, setResumenAdmin] = useState(null);

  const tieneAdmin = paneles.some((p) => p.key === 'admin');

  // Subtítulo del panel admin con las herramientas reales del usuario. Sale del
  // mismo catálogo que alimenta el menú del panel, así que no agrega endpoints.
  const fetchResumenAdmin = useCallback(async () => {
    if (!tieneAdmin || !token) return;
    try {
      const resp = await fetch(`${API_URL}/api/admin/tool-catalog`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const catalogo = await resp.json();
      const mias = catalogo.filter((t) => hasAdminTool(t.key)).map((t) => t.label);
      setResumenAdmin(resumirHerramientas(mias));
    } catch {
      /* silencioso: si falla, queda la descripción por defecto */
    }
  }, [tieneAdmin, token, hasAdminTool]);

  useEffect(() => {
    fetchResumenAdmin();
  }, [fetchResumenAdmin]);

  const primerNombre = user?.name?.split(' ')[0] || '';

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-2xl" data-testid="paneles-page">

        {/* Encabezado — mismo bloque que WelcomeMessage */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-3">
              <img src="/icono-sur40.png" alt="Sur 4.0" className="h-14 w-14 rounded-xl" />
              <div className="text-left">
                <p className="text-2xl font-extrabold leading-none text-[#00A1DF]">
                  Sur <span className="text-foreground">4.0</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">Operaciones Inteligentes</p>
              </div>
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-2">
            Hola{primerNombre ? `, ${primerNombre}` : ''}
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            {paneles.length > 0
              ? 'Elegí con qué panel querés trabajar.'
              : 'Tu usuario todavía no tiene accesos asignados.'}
          </p>
        </div>

        {paneles.length === 0 ? (
          /* Sin áreas ni herramientas: no hay a dónde entrar. */
          <Card className="border-border/50">
            <CardContent className="p-8 flex flex-col items-center text-center gap-3">
              <AlertCircle className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground max-w-sm">
                Pedile a un administrador que te habilite áreas de trabajo o
                herramientas del panel de administración.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {paneles.map((panel) => {
              const ui = PANEL_UI[panel.key] || PANEL_UI.diagnostico;
              const Icon = ui.icon;
              const descripcion =
                panel.key === 'admin' && resumenAdmin ? resumenAdmin : panel.description;

              return (
                <Card
                  key={panel.key}
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer hover:shadow-md transition-all border-border/50 hover:border-[#00B7D4]/30 group focus:outline-none focus:ring-2 focus:ring-[#00B7D4] focus:ring-offset-2"
                  onClick={() => navigate(panel.path)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(panel.path);
                    }
                  }}
                  data-testid={`panel-${panel.key}`}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className={`${ui.iconBg} p-2 rounded-lg shrink-0`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium mb-1 group-hover:text-[#00B7D4] transition-colors">
                        {panel.label}
                      </h3>
                      <p className="text-sm text-muted-foreground">{descripcion}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pie */}
        <div className="mt-8 flex flex-col items-center gap-1">
          <p className="text-xs text-muted-foreground">
            {user?.name} · <span className="capitalize">{user?.role}</span>
          </p>
          <Button
            variant="ghost"
            className="gap-2 text-muted-foreground hover:text-destructive"
            onClick={logout}
            data-testid="paneles-logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </Button>
        </div>

      </div>
    </div>
  );
};

export default PanelesPage;
